import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Error as FoundationError, isError } from 'fl-foundation';
import { AuthorizationData } from 'fl-platform-authorizer';
import { decrypt, isTokenExpired } from 'qp-utility';
import { Observable } from 'rxjs';
import { LocalStorageKeys } from '../../LocalStorageKeys';
import { SessionStorageKeys } from '../../SessionStorageKeys';
import { isLoggedIn } from '../../utils/LoginUtils';
import { saveAccessToken } from '../../utils/tokenUtils';
import { ConfigService } from '../config/config.service';
import { PlaybackService } from '../playback/playback.service';
import { LocalStorageService } from '../local-storage/local-storage.service';

/**
 * Auth0 management
 */
@Injectable()
export class AuthService {
  constructor(private http: HttpClient, private configService: ConfigService, private playbackService: PlaybackService, private localStorage: LocalStorageService) {}

  async getAuthData(): Promise<AuthorizationData> {
    try {
      this.playbackService.resetPlayback();

      const confData = sessionStorage.getItem(SessionStorageKeys.CONF_START);
      const splashStart = sessionStorage.getItem(SessionStorageKeys.ELAPSED_SPLASH)
        ? parseInt(sessionStorage.getItem(SessionStorageKeys.ELAPSED_SPLASH), 10)
        : 0;
      // sessionStorage.clear();
      sessionStorage.setItem(SessionStorageKeys.ELAPSED_SPLASH, '' + splashStart);
      sessionStorage.setItem(SessionStorageKeys.CONF_START, confData);

      const authData: AuthorizationData | FoundationError = await this.playbackService.initialiseSDK();
      if (isError(authData)) {
        return Promise.reject();
      } else {
        return Promise.resolve(authData);
      }
    } catch (error) {
      // clear local storage and reset SDK as anonymous user
      this.playbackService.resetPlayback();
      this.playbackService.intializeConfigurations();
      await this.playbackService.initialiseSDK();
      return Promise.reject(error);
    }
  }

  async refreshAuth0Token(): Promise<void> {
    if (isLoggedIn(this.localStorage)) {
      if (isTokenExpired(this.localStorage.getItem(LocalStorageKeys.REFRESH_TOKEN_EXP))) {
        const response = await this.makeRefreshTokenCall(this.getCachedRefreshToken());
        saveAccessToken(response, this.localStorage);
        return Promise.resolve();
      }
    }
    return await Promise.resolve();
  }

  getDeviceCode(): Observable<any> {
    const { auth0ClientID, auth0Url } = this.configService.applicationConfig;
    const httpOptions: any = {
      headers: new HttpHeaders({
        'content-type': 'application/x-www-form-urlencoded',
      }),
    };
    const body = new HttpParams()
      .set('client_id', decrypt(auth0ClientID))
      .set('scope', 'openid email profile offline_access')
      .set('audience', auth0Url + '/api/v2/');
    return this.http.post(auth0Url + '/oauth/device/code', body.toString(), httpOptions);
  }

  pollForToken(deviceCode: string): Observable<any> {
    const { auth0ClientID, auth0Url } = this.configService.applicationConfig;
    const httpOptions: any = {
      headers: new HttpHeaders({
        'content-type': 'application/x-www-form-urlencoded',
      }),
    };
    const body = new HttpParams()
      .set('client_id', decrypt(auth0ClientID))
      .set('device_code', deviceCode)
      .set('grant_type', 'urn:ietf:params:oauth:grant-type:device_code');
    return this.http.post(auth0Url + '/oauth/token', body, httpOptions);
  }

  makeRefreshTokenCall(refreshToken): Promise<any> {
    const { auth0ClientID, auth0Url, auth0ClientSecret } = this.configService.applicationConfig;
    const httpOptions: any = {
      headers: new HttpHeaders({
        'content-type': 'application/x-www-form-urlencoded',
      }),
    };
    const body = new HttpParams()
      .set('client_id', decrypt(auth0ClientID))
      .set('client_secret', decrypt(auth0ClientSecret))
      .set('refresh_token', refreshToken)
      .set('audience', auth0Url + '/userinfo/')
      .set('grant_type', 'refresh_token');
    return this.http.post(auth0Url + '/oauth/token', body, httpOptions).toPromise();
  }

  refreshTokenRequest(): Observable<any> {
    const refreshToken = this.getCachedRefreshToken();
    const { auth0ClientID, auth0Url, auth0ClientSecret } = this.configService.applicationConfig;
    const httpOptions: any = {
      headers: new HttpHeaders({
        'content-type': 'application/x-www-form-urlencoded',
      }),
    };
    const body = new HttpParams()
      .set('client_id', decrypt(auth0ClientID))
      .set('client_secret', decrypt(auth0ClientSecret))
      .set('refresh_token', refreshToken)
      .set('audience', auth0Url + '/userinfo/')
      .set('grant_type', 'refresh_token');
    return this.http.post(auth0Url + '/oauth/token', body, httpOptions);
  }

  private getCachedRefreshToken() {
    return this.localStorage.getItem(LocalStorageKeys.REFRESH_TOKEN);
  }
}
